import { useMemo } from "react";
import { usePermissions } from "../../../context/PermissionContext";
import { getRole } from "../../../common/function";

export interface ExpenseAccess {
  canCreate: boolean;
  canView: boolean;
  canApprove: boolean;
  canReimburse: boolean;
  canAdmin: boolean;
}

/**
 * Defensive permission hook for the expense module.
 *
 * V033 ships proper RBAC permissions (`expense.create`, `expense.view`,
 * `expense.approve`, `expense.reimburse`, `expense.admin`). During the
 * migration window before V033 reaches every environment, fall back to
 * role-based checks so the UI keeps working.
 *
 * Per design doc §8.4.
 */
export function useExpenseAccess(): ExpenseAccess {
  const { hasPermission } = usePermissions();
  const role = getRole() ?? "";
  const isAdminRole = ["Admin", "Super Admin"].includes(role);

  // [UAT-R3 Finding 1] The enterprise catalog now carries dedicated EXPENSE_* codes
  // (db-enterprise V065 — parity with the legacy V033 expense.* RBAC) and expense.controller.ts
  // guards every route with them (EXPENSE_APPROVE replaced the borrowed INVOICE_APPROVE,
  // EXPENSE_ADMIN replaced ADMIN_MANAGE_SETTINGS). Gate the UI on the SAME codes the API
  // enforces, so granting/revoking one in the Update-User Permissions tab flips the affordance
  // together with the 403. The legacy dotted keys are kept for environments still serving the
  // V033 vocabulary; isAdminRole stays as the defensive fallback (legacy UX: admins always see
  // the module).
  return useMemo(
    () => ({
      canCreate: hasPermission("EXPENSE_CREATE") || hasPermission("expense.create") || isAdminRole,
      canView: hasPermission("EXPENSE_VIEW") || hasPermission("expense.view") || isAdminRole,
      canApprove:
        hasPermission("EXPENSE_APPROVE") || hasPermission("expense.approve") || isAdminRole,
      canReimburse:
        hasPermission("EXPENSE_REIMBURSE") || hasPermission("expense.reimburse") || isAdminRole,
      canAdmin: hasPermission("EXPENSE_ADMIN") || hasPermission("expense.admin") || isAdminRole,
    }),
    [hasPermission, isAdminRole]
  );
}
